import React, { useState, useEffect, useRef } from 'react';
import {
    FaBook, FaCalendarAlt, FaGraduationCap, FaUserTie, FaCog,
    FaArrowRight, FaArrowLeft, FaSave, FaSpinner, FaCheckCircle, FaExclamationTriangle,
    FaUsers, FaClipboardList, FaPlus, FaTrash, FaTimes, FaToggleOn, FaToggleOff, FaFileAlt
} from 'react-icons/fa';
import CurriculumFormsPanel from './CurriculumFormsPanel';
import { normalizeUsers } from '../utils/userUtils';
import { getApiUrl } from '../utils/config';
import { buildCurriculumDateDefaults, splitDateRangeIntoRotations, getRotationRangeFromRotations } from '../utils/dateUtils';
import {
    MATCH_BY_INSTITUTION,
    buildCurriculumDefaultsFromRank,
    findRank,
    getMatchByMeta,
    normalizeMatchBy,
    preceptorServesRank
} from '../utils/rankUtils';
import '../styles/ManageCurriculums.css';

// Stable colour per rank name so badges stay consistent as ranks are added.
const RANK_BADGE_PALETTE = ['#1e3a8a', '#7c2d12', '#059669', '#7c3aed', '#b45309', '#0e7490'];

const getRankBadgeColor = (rank) => {
    const key = (rank || '').toLowerCase();
    if (!key) return '#475569';
    let hash = 0;
    for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) % 997;
    return RANK_BADGE_PALETTE[hash % RANK_BADGE_PALETTE.length];
};

const FIELD_STYLE = {
    width: '100%',
    padding: '0.875rem',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    fontSize: '1rem',
    outline: 'none',
    background: 'white',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    color: '#1f2937',
};

const focusField = (e) => {
    e.target.style.borderColor = '#4a90e2';
    e.target.style.boxShadow = '0 0 0 3px rgba(74,144,226,0.15)';
};
const blurField = (e) => {
    e.target.style.borderColor = '#e5e7eb';
    e.target.style.boxShadow = 'none';
};

const LABEL_STYLE = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.45rem',
    marginBottom: '0.5rem',
    fontWeight: 700,
    color: '#374151',
    fontSize: '0.9rem',
};

const CARD_STYLE = {
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '16px',
    padding: '1.5rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
};

// Safely convert possible MongoDB numeric wrapper objects to JS numbers
const safeNumber = (v, fallback = 0) => {
    if (v == null) return fallback;
    if (typeof v === 'object') {
        if ('$numberInt' in v) return parseInt(v.$numberInt, 10) || fallback;
        if ('$numberLong' in v) return parseInt(v.$numberLong, 10) || fallback;
        if ('$numberDouble' in v) return Math.floor(Number(v.$numberDouble) || fallback);
    }
    const n = parseInt(v, 10);
    return Number.isNaN(n) ? fallback : n;
};

/* ── Searchable multi-select used for students & preceptors ── */
const MultiSelectDropdown = ({ options, selectedIds, onChange, placeholder, loading, type, filterRankId, filterRankName, allRanks = [] }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    useEffect(() => {
        if (open && inputRef.current) setTimeout(() => inputRef.current?.focus(), 80);
    }, [open]);

    // Only offer users eligible for the curriculum's rank: preceptors must list it
    // in rankIds, students must carry its name (unranked students stay visible).
    const rankEligible = options.filter(o => {
        if (type === 'preceptor') {
            return !filterRankId || preceptorServesRank(o, filterRankId, allRanks);
        }
        if (!filterRankName) return true;
        const optionRank = o.rank?.toLowerCase();
        return !optionRank || optionRank === filterRankName.toLowerCase();
    });

    const filtered = rankEligible.filter(o => {
        const firstName = o.firstname || '';
        const lastName = o.lastname || '';
        const fullName = `${firstName} ${lastName}`.toLowerCase();
        const searchLower = search.toLowerCase();
        const id = (o._id || o.id || '').toString().toLowerCase();
        const email = (o.email || '').toLowerCase();
        return fullName.includes(searchLower) ||
               id.includes(searchLower) ||
               email.includes(searchLower);
    });

    const selectedItems = selectedIds.map(id => options.find(o => (o._id || o.id) === id)).filter(Boolean);

    const toggle = (id, e) => {
        e?.stopPropagation();
        const currentIds = Array.isArray(selectedIds) ? selectedIds : [];
        const newIds = currentIds.includes(id) 
            ? currentIds.filter(x => x !== id) 
            : [...currentIds, id];
        onChange(newIds);
        inputRef.current?.focus();
    };

    const selectAll = (e) => {
        e.stopPropagation();
        const allIds = filtered.map(o => o._id || o.id);
        const currentIds = Array.isArray(selectedIds) ? selectedIds : [];
        const allSelected = allIds.every(id => currentIds.includes(id));
        onChange(allSelected 
            ? currentIds.filter(id => !allIds.includes(id)) 
            : [...new Set([...currentIds, ...allIds])]
        );
        inputRef.current?.focus();
    };

    return (
        <div ref={ref} style={{ position: 'relative', marginBottom: '1.75rem' }}>
            {selectedItems.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.6rem' }}>
                    {selectedItems.map(item => (
                        <span key={item._id} style={{
                            display: 'flex', alignItems: 'center', gap: '0.35rem',
                            background: 'white', border: '1px solid #d1d5db',
                            padding: '0.3rem 0.65rem', borderRadius: '20px',
                            fontSize: '0.8rem', fontWeight: 600, color: '#374151',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                        }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: getRankBadgeColor(item.rank), display: 'inline-block', flexShrink: 0 }} />
                            {item.firstname} {item.lastname}
                            {item.rank && <span style={{ fontSize: '0.65rem', fontWeight: 700, background: getRankBadgeColor(item.rank), color: 'white', padding: '0.05rem 0.35rem', borderRadius: '10px', textTransform: 'uppercase' }}>{item.rank}</span>}
                            <button onClick={(e) => { e.stopPropagation(); toggle(item._id); }} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '0.85rem', padding: 0, display: 'flex', alignItems: 'center' }}>×</button>
                        </span>
                    ))}
                </div>
            )}
            <div onClick={() => setOpen(v => !v)} style={{
                ...FIELD_STYLE, cursor: 'pointer', display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', padding: '0.75rem 1rem'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {selectedIds.length > 0 && <span style={{ background: '#3b82f6', color: 'white', borderRadius: '12px', padding: '0.1rem 0.5rem', fontSize: '0.75rem', fontWeight: 700 }}>{selectedIds.length}</span>}
                    <span style={{ color: selectedIds.length > 0 ? '#374151' : '#9ca3af', fontSize: '0.875rem' }}>
                        {loading ? 'Loading...' : selectedIds.length > 0 ? `${selectedIds.length} ${type}(s) selected` : placeholder}
                    </span>
                </div>
                <span style={{ color: '#9ca3af', transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0)' }}>▼</span>
            </div>

            {open && !loading && (
                <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999, background: 'white',
                    border: '2px solid #e5e7eb', borderRadius: '12px', marginTop: '0.25rem',
                    boxShadow: '0 12px 28px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', overflow: 'hidden'
                }}>
                    <div style={{ padding: '0.65rem', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder={`Search ${type}s by name, ID, or email…`}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            onClick={e => e.stopPropagation()}
                            style={{ ...FIELD_STYLE, padding: '0.6rem 0.875rem', fontSize: '0.85rem', border: '1.5px solid #e5e7eb' }}
                            onFocus={focusField} onBlur={blurField}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
                            <button onClick={selectAll} style={{
                                background: selectedIds.length === filtered.length && filtered.length > 0 ? 'linear-gradient(135deg,#ef4444,#dc2626)' : 'linear-gradient(135deg,#3b82f6,#2563eb)',
                                border: 'none', borderRadius: '8px', padding: '0.4rem 0.75rem', color: 'white',
                                fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem'
                            }}>
                                <FaCheckCircle style={{ fontSize: '0.7rem' }} />
                                {selectedIds.length === filtered.length && filtered.length > 0 ? 'Deselect All' : `Select All (${filtered.length})`}
                            </button>
                            {search && <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{filtered.length} found</span>}
                        </div>
                    </div>
                    <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                        {filtered.length === 0 ? (
                            <div style={{ padding: '1.5rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.875rem' }}>
                                {search ? `No ${type}s match "${search}"` : `No ${type}s available`}
                            </div>
                        ) : filtered.map(option => {
                            const optionId = option._id || option.id;
                            const currentIds = Array.isArray(selectedIds) ? selectedIds : [];
                            const isSelected = currentIds.includes(optionId);
                            return (
                                <div key={optionId} onClick={(e) => toggle(optionId, e)} style={{
                                    padding: '0.8rem', cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
                                    background: isSelected ? '#eff6ff' : 'white',
                                    borderLeft: isSelected ? '4px solid #3b82f6' : '4px solid transparent',
                                    display: 'flex', alignItems: 'center', gap: '0.75rem', transition: 'all 0.15s'
                                }}
                                    onMouseEnter={e => { if (!isSelected) { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderLeft = '4px solid #cbd5e1'; } else e.currentTarget.style.background = '#dbeafe'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = isSelected ? '#eff6ff' : 'white'; e.currentTarget.style.borderLeft = isSelected ? '4px solid #3b82f6' : '4px solid transparent'; }}
                                >
                                    <div style={{ width: 20, height: 20, borderRadius: 6, border: isSelected ? '2px solid #3b82f6' : '2px solid #cbd5e1', background: isSelected ? '#3b82f6' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.7rem', fontWeight: 700, flexShrink: 0 }}>
                                        {isSelected ? '✓' : ''}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 600, fontSize: '0.875rem', color: isSelected ? '#1e40af' : '#111827' }}>
                                            {option.firstname} {option.lastname}
                                            {option.rank && <span style={{ background: getRankBadgeColor(option.rank), color: 'white', padding: '0.1rem 0.4rem', borderRadius: '10px', fontSize: '0.67rem', fontWeight: 700, textTransform: 'uppercase' }}>{option.rank}</span>}
                                        </div>
                                        <div style={{ fontSize: '0.73rem', color: isSelected ? '#3b82f6' : '#6b7280' }}>ID: {optionId} • {option.email || '—'}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div style={{ padding: '0.65rem', borderTop: '1px solid #f1f5f9', background: '#fafbfc', display: 'flex', justifyContent: 'flex-end' }}>
                        <button onClick={(e) => { e.stopPropagation(); setOpen(false); }} style={{
                            background: 'linear-gradient(135deg,#10b981,#059669)', border: 'none', borderRadius: '8px',
                            padding: '0.45rem 1.1rem', color: 'white', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: '0.4rem'
                        }}>
                            <FaCheckCircle style={{ fontSize: '0.75rem' }} /> Done
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

/* ── Entity chip-select (specialities / institutions for constraints) ── */
const EntityChipSelect = ({ items, selectedIds, onChange, loading, placeholder }) => {
    const [search, setSearch] = useState('');
    const filtered = items.filter(i => (i.name || i.speciality || '').toLowerCase().includes(search.toLowerCase()));
    return (
        <div>
            <input type="text" placeholder={placeholder || 'Search…'} value={search} onChange={e => setSearch(e.target.value)}
                style={{ ...FIELD_STYLE, padding: '0.5rem 0.75rem', fontSize: '0.82rem', marginBottom: '0.5rem', border: '1.5px solid #e5e7eb' }}
                onFocus={focusField} onBlur={blurField} />
            <div style={{ maxHeight: '150px', overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: '0.35rem', padding: '0.5rem', border: '1px solid #e5e7eb', borderRadius: '10px', background: '#f9fafb' }}>
                {loading ? <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>Loading…</span> :
                    filtered.length === 0 ? <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>No items found</span> :
                        filtered.map(item => {
                            const sel = selectedIds.includes(item._id);
                            return (
                                <button key={item._id} type="button" onClick={() => onChange(sel ? selectedIds.filter(x => x !== item._id) : [...selectedIds, item._id])}
                                    style={{
                                        padding: '0.3rem 0.7rem', borderRadius: '16px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
                                        border: sel ? '2px solid #3b82f6' : '1.5px solid #d1d5db',
                                        background: sel ? '#dbeafe' : 'white', color: sel ? '#1e40af' : '#374151'
                                    }}>
                                    {item.name || item.speciality}
                                </button>
                            );
                        })}
            </div>
        </div>
    );
};

/* ── Toggle switch ── */
const Toggle = ({ value, onChange, label }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.875rem 1rem', background: value ? '#eff6ff' : '#f9fafb', border: `2px solid ${value ? '#3b82f6' : '#e5e7eb'}`, borderRadius: '12px', cursor: 'pointer', transition: 'all 0.2s' }}
        onClick={() => onChange(!value)}>
        <span style={{ fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>{label}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.8rem', color: value ? '#3b82f6' : '#9ca3af', fontWeight: 600 }}>{value ? 'ON' : 'OFF'}</span>
            <div style={{ width: 44, height: 24, borderRadius: 12, background: value ? '#3b82f6' : '#d1d5db', position: 'relative', transition: 'background 0.2s' }}>
                <div style={{ position: 'absolute', top: 3, left: value ? 23 : 3, width: 18, height: 18, borderRadius: '50%', background: 'white', transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }} />
            </div>
        </div>
    </div>
);

/* ════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════ */
const EditCurriculumModern = ({ curriculum, onSuccess, onCancel, onNotifySuccess, onNotifyError }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [saving, setSaving] = useState(false);
    const [rotationWarning, setRotationWarning] = useState(null);

    /* ── User lists ── */
    const [students, setStudents] = useState([]);
    const [preceptors, setPreceptors] = useState([]);
    const [studentsLoading, setStudentsLoading] = useState(false);
    const [preceptorsLoading, setPreceptorsLoading] = useState(false);

    /* ── Constraint entity lists ── */
    const [specialities, setSpecialities] = useState([]);
    const [institutions, setInstitutions] = useState([]);
    const [specialitiesLoading, setSpecialitiesLoading] = useState(false);
    const [institutionsLoading, setInstitutionsLoading] = useState(false);

    /* ── Stage data ── */
    const editDefaults = buildCurriculumDateDefaults(6);
    const [basicData, setBasicData] = useState({
        name: '',
        start_date: editDefaults.curriculumStartDate,
        end_date: editDefaults.curriculumEndDate,
        rankId: '',
        student_ids: [],
        preceptor_ids: []
    });
    const [configData, setConfigData] = useState({
        rotationsCount: 6,
        rotations: editDefaults.rotations,
        form_registration: editDefaults.formRegistration,
        choicesCount: 4,
        spep_prefs: false, sitePreceptorOnly: false,
        defaultFullTimeHrs: { start_time: '08:00', end_time: '16:00' },
        defaultPartTimeHrs: { start_time: '08:00', end_time: '12:00' }
    });
    // One constraint list; its entity type follows the selected rank's matchBy.
    const [constraintsData, setConstraintsData] = useState({ constraints: [] });
    const [ranks, setRanks] = useState([]);

    const token = () => localStorage.getItem('access_token') || '';
    const authH = { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' };

    const notify = { success: msg => onNotifySuccess?.(msg), error: msg => onNotifyError?.(msg) };

    const parseDateInput = (dateValue) => {
        if (!dateValue) return '';
        if (typeof dateValue === 'string' && dateValue.includes('T')) {
            return dateValue.split('T')[0];
        }
        if (typeof dateValue === 'string' || typeof dateValue === 'number' || dateValue instanceof Date) {
            const parsed = new Date(dateValue);
            return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().split('T')[0];
        }
        return '';
    };

    const toApiISOString = (dateValue, endOfDay = false) => {
        if (!dateValue) return '';
        if (typeof dateValue === 'string' && dateValue.includes('T')) {
            return dateValue;
        }
        const parsed = new Date(`${dateValue}T${endOfDay ? '23:59:59' : '00:00:00'}`);
        return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString();
    };

    /* ── Init ── */
    useEffect(() => {
        if (!curriculum) return;
        initFromCurriculum();
        loadStudents();
        loadPreceptors();
        loadSpecialities();
        loadInstitutions();
        loadRanks();
    }, [curriculum?._id]);

    // The rank currently selected, and what its curricula match preceptors on.
    const selectedRank = findRank(ranks, basicData.rankId);
    const matchBy = normalizeMatchBy(selectedRank?.matchBy);
    const matchByMeta = getMatchByMeta(matchBy);
    const constraintEntities = matchBy === MATCH_BY_INSTITUTION ? institutions : specialities;
    const constraintEntitiesLoading = matchBy === MATCH_BY_INSTITUTION ? institutionsLoading : specialitiesLoading;

    /**
     * Switch the curriculum to a different rank. Its rotation defaults are offered
     * to the config step, and constraints are cleared when the match type changes
     * because their entities are no longer the right type.
     */
    const handleRankChange = (rankId) => {
        const previousMatchBy = matchBy;
        const nextRank = findRank(ranks, rankId);

        setBasicData(prev => ({ ...prev, rankId }));

        if (nextRank && normalizeMatchBy(nextRank.matchBy) !== previousMatchBy) {
            setConstraintsData({ constraints: [] });
        }
    };

    /** Pull this rank's rotation count and date ranges into the config step. */
    const applyRankDefaults = () => {
        if (!selectedRank) return;
        const defaults = buildCurriculumDefaultsFromRank(selectedRank);
        setConfigData(prev => ({
            ...prev,
            rotationsCount: defaults.rotationsCount,
            rotations: defaults.hasDates
                ? defaults.rotations
                : ensureRotations(defaults.rotationsCount, prev.rotations)
        }));
        if (defaults.hasDates) {
            setBasicData(prev => ({ ...prev, start_date: defaults.startDate, end_date: defaults.endDate }));
        }
        notify.success(`Applied ${selectedRank.name} rotation defaults`);
    };

    const initFromCurriculum = async () => {
        const c = curriculum;
        const studentIds = Array.isArray(c.student_ids) ? c.student_ids
            : Array.isArray(c.students) ? c.students.map(s => (typeof s === 'string' ? s : s._id || s)) : [];
        const preceptorIds = Array.isArray(c.preceptor_ids) ? c.preceptor_ids
            : Array.isArray(c.preceptors) ? c.preceptors.map(p => (typeof p === 'string' ? p : p._id || p)) : [];

        const defaults = buildCurriculumDateDefaults(safeNumber(c?.config?.rotationsNumber ?? c?.config?.rotationsCount, configData.rotationsCount || 6));

        setBasicData({
            name: c.name || '',
            start_date: c.start_date ? c.start_date.split('T')[0] : defaults.curriculumStartDate,
            end_date: c.end_date ? c.end_date.split('T')[0] : defaults.curriculumEndDate,
            // Legacy curricula carry only the rank name; findRank resolves either.
            rankId: c.rankId || c.rank || '',
            student_ids: studentIds,
            preceptor_ids: preceptorIds,
        });

        try {
            const cfgRes = await fetch(getApiUrl(`config/get_configuration_by_curriculum/${c._id}`), { headers: authH });
            if (cfgRes.ok) {
                const raw = await cfgRes.json();
                const cfg = Array.isArray(raw) ? raw[0] : raw;
                if (cfg) {
                        const cfgRotationCount = safeNumber(cfg.rotationsNumber ?? cfg.rotationsCount, 6);
                        const cfgDefaults = buildCurriculumDateDefaults(cfgRotationCount);
                    const hasCustomRotations = Array.isArray(cfg.rotations) && cfg.rotations.length > 0;
                    const storedRegistration = cfg.form_registration || cfg.formRegistration || null;
                    const hasCustomRegistration = storedRegistration && (storedRegistration.startDate || storedRegistration.endDate);

                    setConfigData({
                        rotationsCount: cfgRotationCount,
                        rotations: hasCustomRotations
                            ? cfg.rotations.map(rotation => ({
                                startDate: parseDateInput(rotation?.startDate),
                                endDate: parseDateInput(rotation?.endDate)
                            }))
                            : cfgDefaults.rotations,
                        form_registration: hasCustomRegistration
                            ? {
                                startDate: parseDateInput(storedRegistration?.startDate),
                                endDate: parseDateInput(storedRegistration?.endDate)
                            }
                            : cfgDefaults.formRegistration,
                        choicesCount: safeNumber(cfg.choicesNumber ?? cfg.choicesCount, 4),
                        spep_prefs: cfg.spep_prefs || false,
                        sitePreceptorOnly: cfg.sitePreceptorOnly || false,
                        defaultFullTimeHrs: cfg.defaultFullTimeHrs || { start_time: '08:00', end_time: '16:00' },
                        defaultPartTimeHrs: cfg.defaultPartTimeHrs || { start_time: '08:00', end_time: '12:00' },
                    });
                }
            }
        } catch { /* use defaults */ }

        try {
            const conRes = await fetch(getApiUrl(`constraints/get_constraints_by_curriculum/${c._id}`), { headers: authH });
            if (conRes.ok) {
                const raw = await conRes.json();
                const con = Array.isArray(raw) ? raw[0] : raw;
                if (con) {
                    // Prefer the generalized list; fall back to whichever legacy
                    // bucket a pre-migration doc happens to have filled.
                    const list = Array.isArray(con.constraints) && con.constraints.length
                        ? con.constraints
                        : (con.pharmdConstraints?.length ? con.pharmdConstraints : (con.spepConstraints || []));
                    setConstraintsData({
                        constraints: (list || []).map((x, i) => ({
                            id: i,
                            ...x,
                            entities: (x.entities || []).map(e => (typeof e === 'string' ? e : (e.id || e._id))).filter(Boolean),
                        })),
                    });
                }
            }
        } catch { /* use defaults */ }
    };

    const loadStudents = async () => {
        setStudentsLoading(true);
        try {
            const res = await fetch(getApiUrl('crud/get_all_students_basic_info/'), { headers: authH });
            if (res.ok) {
                const data = await res.json();
                if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Students API response:', data);
                let studentsList = data;
                if (data && typeof data === 'object' && !Array.isArray(data)) {
                    studentsList = data.data || data.students || [];
                }
                setStudents(normalizeUsers(Array.isArray(studentsList) ? studentsList : []));
            } else {
                if (import.meta.env.VITE_DEBUG_MODE === 'true') console.warn('Failed to load students:', res.status);
            }
        } catch (err) {
            console.error('Error loading students:', err);
        } finally { 
            setStudentsLoading(false); 
        }
    };
    const loadPreceptors = async () => {
        setPreceptorsLoading(true);
        try {
            const res = await fetch(getApiUrl('crud/get_all_preceptors_basic_info/'), { headers: authH });
            if (res.ok) {
                const data = await res.json();
                if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Preceptors API response:', data);
                let preceptorsList = data;
                if (data && typeof data === 'object' && !Array.isArray(data)) {
                    preceptorsList = data.data || data.preceptors || [];
                }
                setPreceptors(normalizeUsers(Array.isArray(preceptorsList) ? preceptorsList : []));
            } else {
                if (import.meta.env.VITE_DEBUG_MODE === 'true') console.warn('Failed to load preceptors:', res.status);
            }
        } catch (err) {
            console.error('Error loading preceptors:', err);
        } finally { 
            setPreceptorsLoading(false); 
        }
    };
    const loadSpecialities = async () => {
        setSpecialitiesLoading(true);
        try {
            const res = await fetch(getApiUrl('crud/get_full_specialities'), { headers: authH });
            if (res.ok) {
                const data = await res.json();
                setSpecialities(Array.isArray(data) ? data : (data?.data || data?.specialities || []));
            }
        } catch (err) { 
            console.error('Error loading specialities:', err);
        } finally { 
            setSpecialitiesLoading(false); 
        }
    };
    const loadRanks = async () => {
        try {
            const res = await fetch(getApiUrl('ranks/ranks'), { headers: authH });
            if (res.ok) {
                const data = await res.json();
                setRanks(Array.isArray(data) ? data : []);
            }
        } catch (err) {
            console.error('Error loading ranks:', err);
        }
    };
    const loadInstitutions = async () => {
        setInstitutionsLoading(true);
        try {
            const res = await fetch(getApiUrl('crud/get_full_institutions'), { headers: authH });
            if (res.ok) {
                const data = await res.json();
                setInstitutions(Array.isArray(data) ? data : (data?.data || data?.institutions || []));
            }
        } catch (err) { 
            console.error('Error loading institutions:', err);
        } finally { 
            setInstitutionsLoading(false); 
        }
    };

    /* ── Rotation helpers ── */
    const ensureRotations = (count, existing) => {
        const arr = [];
        for (let i = 0; i < count; i++) {
            arr.push(existing[i] || { startDate: '', endDate: '' });
        }
        return arr;
    };

    const updateRotationCount = (newCount) => {
        const count = Math.max(1, Math.min(16, newCount));
        setConfigData(prev => {
            const rotationRange = getRotationRangeFromRotations(prev.rotations);
            return {
                ...prev,
                rotationsCount: count,
                rotations: rotationRange
                    ? splitDateRangeIntoRotations(rotationRange.startDate, rotationRange.endDate, count)
                    : ensureRotations(count, prev.rotations)
            };
        });
    };

    const updateRotationDate = (index, field, value) => {
        setConfigData(prev => {
            const rotations = [...prev.rotations];
            rotations[index] = { ...rotations[index], [field]: value };
            return { ...prev, rotations };
        });
    };

    /* ── Constraint helpers ── */
    const addConstraint = () => {
        setConstraintsData(p => ({
            constraints: [
                ...p.constraints,
                { id: Date.now(), minimumNumber: 1, fromRotation: 1, toRotation: configData.rotationsCount, entities: [] }
            ]
        }));
    };

    const removeConstraint = (id) => {
        setConstraintsData(p => ({ constraints: p.constraints.filter(c => c.id !== id) }));
    };

    const updateConstraint = (id, field, value) => {
        setConstraintsData(p => ({
            constraints: p.constraints.map(c => (c.id === id ? { ...c, [field]: value } : c))
        }));
    };

    /* ── Validation ── */
    const validateStep = () => {
        if (currentStep === 1) {
            if (!basicData.name.trim()) { notify.error('Curriculum name is required'); return false; }
            if (!basicData.start_date) { notify.error('Start date is required'); return false; }
            if (!basicData.end_date) { notify.error('End date is required'); return false; }
            if (new Date(basicData.start_date) >= new Date(basicData.end_date)) { notify.error('End date must be after start date'); return false; }
            if (!basicData.rankId) { notify.error('Please select a rank'); return false; }
        }
        return true;
    };

    const toApiDT = (d, endOfDay = false) => {
        if (!d) return '';
        if (d.includes('T')) return d;
        return `${d}T${endOfDay ? '23:59:59' : '00:00:00'}`;
    };

    const submitConfigStage = async (cid, confirmConfig = false) => {
        const rotations = ensureRotations(configData.rotationsCount, configData.rotations);
        const validRotations = rotations.filter(r => r.startDate && r.endDate);

        if (validRotations.length === 0) {
            throw new Error('No rotation dates found. Please fill in start and end dates for your rotations in Stage 2.');
        }

        const years = validRotations.map(r => [r.startDate, r.endDate]).flat().filter(Boolean).map(d => new Date(d).getFullYear());
        const minY = Math.min(...(years.length ? years : [new Date().getFullYear()]));
        const maxY = Math.max(...(years.length ? years : [new Date().getFullYear()]));
        const year = minY === maxY ? `${minY}` : `${minY}/${maxY}`;

        const regStart = configData.form_registration?.startDate;
        const regEnd = configData.form_registration?.endDate;
        if (!regStart || !regEnd) {
            throw new Error('Form registration dates are required. Please set them in Stage 2.');
        }

        const cfgParams = new URLSearchParams({
            rotationsNumber: validRotations.length,
            choicesNumber: configData.choicesCount || 4,
            spep_prefs: configData.spep_prefs ? 'true' : 'false',
            sitePreceptorOnly: configData.sitePreceptorOnly ? 'true' : 'false',
        });

        if (confirmConfig) {
            cfgParams.set('confirm', 'true');
        }

        const cfgRes = await fetch(getApiUrl(`config/set_configuration_by_curriculum/${cid}?${cfgParams}`), {
            method: 'PUT', headers: authH,
            body: JSON.stringify({
                year,
                rotations: validRotations.map(r => ({
                    startDate: toApiISOString(r.startDate),
                    endDate: toApiISOString(r.endDate, true)
                })),
                form_registration: {
                    startDate: toApiISOString(regStart),
                    endDate: toApiISOString(regEnd, true)
                },
                formRegistration: {
                    startDate: toApiISOString(regStart),
                    endDate: toApiISOString(regEnd, true)
                },
                defaultFullTimeHrs: {
                    start_time: configData.defaultFullTimeHrs?.start_time || '08:00',
                    end_time: configData.defaultFullTimeHrs?.end_time || '16:00',
                },
                defaultPartTimeHrs: {
                    start_time: configData.defaultPartTimeHrs?.start_time || '08:00',
                    end_time: configData.defaultPartTimeHrs?.end_time || '12:00',
                },
            })
        });

        const cfgText = await cfgRes.text();
        let cfgData = null;
        try {
            cfgData = cfgText ? JSON.parse(cfgText) : null;
        } catch {
            cfgData = null;
        }

        if (cfgData?.status === 'warning' || cfgData?.requires_confirmation) {
            setRotationWarning(cfgData);
            return false;
        }

        if (!cfgRes.ok) {
            const errorMessage = cfgData?.detail || cfgData?.message || cfgText || cfgRes.statusText || 'Config update failed';
            throw new Error(errorMessage);
        }

        return true;
    };

    /* ── Submit ── */
    const handleSubmit = async (confirmConfig = false) => {
        setSaving(true);
        setRotationWarning(null);
        const cid = curriculum._id;
        try {
            /* 1 – Basic info */
            const params = new URLSearchParams({
                curriculum_id: cid,
                name: basicData.name,
                start_date: toApiDT(basicData.start_date),
                end_date: toApiDT(basicData.end_date, true),
                rank_id: basicData.rankId,
            });
            const basicRes = await fetch(getApiUrl(`curriculum/update_curriculum/?${params}`), {
                method: 'PUT', headers: authH
            });
            if (!basicRes.ok) {
                const txt = await basicRes.text();
                // "No changes were made" is acceptable — values may be identical
                if (!txt.includes('No changes')) {
                    throw new Error(`Failed to update curriculum: ${txt}`);
                }
            }

            /* 2 – Config */
            try {
                const configSaved = await submitConfigStage(cid, confirmConfig);
                if (!configSaved) {
                    return;
                }
            } catch (e) {
                if (import.meta.env.VITE_DEBUG_MODE === 'true') console.warn('Config update failed:', e);
                throw new Error(`Config update failed: ${e.message}`);
            }

            /* 3 – Constraints */
            try {
                await fetch(getApiUrl(`constraints/set_constraints_by_curriculum/${cid}`), {
                    method: 'PUT', headers: authH,
                    body: JSON.stringify({
                        // Entity type is derived server-side from the curriculum's rank.
                        constraints: constraintsData.constraints.map(c => ({
                            minimumNumber: Number(c.minimumNumber), fromRotation: Number(c.fromRotation),
                            toRotation: Number(c.toRotation), entities: c.entities,
                        })),
                    })
                });
            } catch (e) { if (import.meta.env.VITE_DEBUG_MODE === 'true') console.warn('Constraints update failed:', e); }

            /* 4 – Users diff */
            try {
                const origStudents = Array.isArray(curriculum.student_ids) ? curriculum.student_ids
                    : Array.isArray(curriculum.students) ? curriculum.students.map(s => typeof s === 'string' ? s : s._id || s) : [];
                const origPreceptors = Array.isArray(curriculum.preceptor_ids) ? curriculum.preceptor_ids
                    : Array.isArray(curriculum.preceptors) ? curriculum.preceptors.map(p => typeof p === 'string' ? p : p._id || p) : [];

                const studentsToAdd = basicData.student_ids.filter(id => !origStudents.includes(id));
                const studentsToRemove = origStudents.filter(id => !basicData.student_ids.includes(id));
                const preceptorsToAdd = basicData.preceptor_ids.filter(id => !origPreceptors.includes(id));
                const preceptorsToRemove = origPreceptors.filter(id => !basicData.preceptor_ids.includes(id));

                if (studentsToRemove.length > 0 || preceptorsToRemove.length > 0) {
                    const body = {};
                    if (studentsToRemove.length) body.student_ids = studentsToRemove;
                    if (preceptorsToRemove.length) body.preceptor_ids = preceptorsToRemove;
                    await fetch(getApiUrl(`curriculum/${cid}/remove_users`), {
                        method: 'DELETE', headers: authH, body: JSON.stringify(body)
                    });
                }
                if (studentsToAdd.length > 0 || preceptorsToAdd.length > 0) {
                    const body = {};
                    if (studentsToAdd.length) body.student_ids = studentsToAdd;
                    if (preceptorsToAdd.length) body.preceptor_ids = preceptorsToAdd;
                    await fetch(getApiUrl(`curriculum/${cid}/add_users`), {
                        method: 'POST', headers: authH, body: JSON.stringify(body)
                    });
                }
            } catch (e) { if (import.meta.env.VITE_DEBUG_MODE === 'true') console.warn('User sync failed:', e); }

            notify.success(confirmConfig ? 'Curriculum configuration updated successfully.' : 'Curriculum updated successfully!');
            onSuccess?.();
        } catch (err) {
            console.error(err);
            notify.error(err.message || 'Failed to update curriculum');
        } finally {
            setSaving(false);
        }
    };

    /* ── Stage meta ── */
    const TOTAL_STEPS = 5;
    const stages = [
        { id: 1, title: 'Basic Info', icon: <FaBook /> },
        { id: 2, title: 'Config', icon: <FaCog /> },
        { id: 3, title: 'Constraints', icon: <FaClipboardList /> },
        { id: 4, title: 'Forms', icon: <FaFileAlt /> },
        { id: 5, title: 'Review', icon: <FaCheckCircle /> },
    ];

    if (!curriculum) return null;

    return (
        <div className="mc-editor-panel mc-transition-up" style={{ marginTop: '1.25rem', padding: 0 }}>
            <div style={{
                background: 'white', borderRadius: '24px',
                display: 'flex', flexDirection: 'column',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.22)',
                overflow: 'hidden',
                border: '1px solid rgba(193,212,233,0.5)',
            }}>

                {/* ── Dark header ── */}
                <div style={{
                    background: 'linear-gradient(135deg, var(--mc-dark-header,#0f2740) 0%, var(--mc-dark-header-soft,#1a3a5c) 100%)',
                    padding: '2rem 2.5rem', color: 'white', position: 'relative',
                }}>
                    <div style={{ marginBottom: '1.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.35rem' }}>
                            <span style={{ background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.28)', padding: '2px 10px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Edit Curriculum</span>
                        </div>
                        <h2 style={{ margin: 0, fontSize: 'clamp(1.5rem,3vw,2rem)', fontWeight: 800, color: 'white' }}>
                            ✏️ {curriculum.name}
                        </h2>
                        <p style={{ margin: '0.4rem 0 0', opacity: 0.85, fontSize: '1rem', fontWeight: 500 }}>
                            Update curriculum details, configuration, and assignments
                        </p>
                    </div>

                    {/* Progress bar */}
                    <div style={{ marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', fontWeight: 600, marginBottom: '0.5rem', opacity: 0.9 }}>
                            <span>Stage {currentStep} of {TOTAL_STEPS}</span>
                            <span>{stages.find(s => s.id === currentStep)?.title}</span>
                        </div>
                        <div style={{ height: 8, borderRadius: 999, background: 'rgba(255,255,255,0.2)', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${(currentStep / TOTAL_STEPS) * 100}%`, background: 'linear-gradient(90deg,#93c5fd,#dbeafe)', transition: 'width 0.3s ease' }} />
                        </div>
                    </div>

                    {/* Stage badges */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {stages.map((s, i) => (
                            <React.Fragment key={s.id}>
                                <div
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '0.6rem',
                                        padding: '0.6rem 0.9rem', borderRadius: 12, cursor: s.id < currentStep ? 'pointer' : 'default',
                                        background: currentStep >= s.id ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.08)',
                                        border: currentStep === s.id ? '2px solid rgba(255,255,255,0.55)' : '2px solid transparent',
                                        transition: 'all 0.25s',
                                    }}
                                    onClick={() => s.id < currentStep && setCurrentStep(s.id)}
                                >
                                    <div style={{
                                        width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        background: currentStep > s.id ? '#4ade80' : currentStep === s.id ? 'white' : 'rgba(255,255,255,0.25)',
                                        color: currentStep > s.id ? 'white' : currentStep === s.id ? '#0f2740' : 'rgba(255,255,255,0.7)',
                                        fontWeight: 700, fontSize: '0.85rem',
                                    }}>
                                        {currentStep > s.id ? <FaCheckCircle /> : s.id}
                                    </div>
                                    <span style={{ fontWeight: 600, fontSize: '0.88rem', opacity: currentStep >= s.id ? 1 : 0.6 }}>{s.title}</span>
                                </div>
                                {i < stages.length - 1 && <FaArrowRight style={{ opacity: 0.4, fontSize: '0.75rem', flexShrink: 0 }} />}
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                {/* ── Content area ── */}
                <div style={{ flex: 1, padding: '2rem 2.5rem', background: 'linear-gradient(180deg,#f8fafc 0%,#eef4fb 100%)', overflowY: 'auto', minHeight: 320 }}>

                    {/* ── STAGE 1: Basic Info ── */}
                    {currentStep === 1 && (
                        <div style={{ maxWidth: 680, margin: '0 auto', animation: 'mc-rise 0.25s ease-out' }}>
                            <h3 style={{ margin: '0 0 0.3rem', fontSize: '1.35rem', fontWeight: 700, color: '#1f2937' }}>Basic Information</h3>
                            <p style={{ margin: '0 0 1.75rem', color: '#6b7280' }}>Update the core details for this curriculum</p>

                            <div style={{ display: 'grid', gap: '1.25rem', marginBottom: '1.5rem' }}>
                                <div>
                                    <label style={LABEL_STYLE}><FaBook style={{ color: '#4a90e2' }} /> Curriculum Name *</label>
                                    <input type="text" value={basicData.name} onChange={e => setBasicData(p => ({ ...p, name: e.target.value }))}
                                        placeholder="e.g., PharmD 2024-2025"
                                        style={FIELD_STYLE} onFocus={focusField} onBlur={blurField} />
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                    <div>
                                        <label style={LABEL_STYLE}><FaCalendarAlt style={{ color: '#4a90e2' }} /> Start Date *</label>
                                        <input type="date" value={basicData.start_date} onChange={e => setBasicData(p => ({ ...p, start_date: e.target.value }))}
                                            style={FIELD_STYLE} onFocus={focusField} onBlur={blurField} />
                                    </div>
                                    <div>
                                        <label style={LABEL_STYLE}><FaCalendarAlt style={{ color: '#4a90e2' }} /> End Date *</label>
                                        <input type="date" value={basicData.end_date} onChange={e => setBasicData(p => ({ ...p, end_date: e.target.value }))}
                                            style={FIELD_STYLE} onFocus={focusField} onBlur={blurField} />
                                    </div>
                                </div>

                                <div>
                                    <label style={LABEL_STYLE}>Rank *</label>
                                    {ranks.length === 0 ? (
                                        <div style={{ padding: '0.75rem', border: '2px dashed #e5e7eb', borderRadius: 12, color: '#9ca3af', fontSize: '0.88rem' }}>
                                            No ranks exist yet — create one from Manage Ranks first.
                                        </div>
                                    ) : (
                                        <>
                                            <select
                                                value={basicData.rankId}
                                                onChange={e => handleRankChange(e.target.value)}
                                                style={FIELD_STYLE}
                                                onFocus={focusField}
                                                onBlur={blurField}
                                            >
                                                <option value="">Select a rank…</option>
                                                {ranks.map(r => (
                                                    <option key={r._id} value={r._id}>
                                                        {r.name} — matched by {r.matchBy}
                                                    </option>
                                                ))}
                                            </select>
                                            {selectedRank && (
                                                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.6rem', marginTop: '0.6rem', fontSize: '0.82rem', color: '#475569' }}>
                                                    <span>
                                                        Matched by {matchByMeta.label.toLowerCase()} • default {selectedRank.defaultRotationsCount} rotations
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={applyRankDefaults}
                                                        style={{
                                                            padding: '0.3rem 0.7rem', borderRadius: 8, border: '1px solid #cbd5e1',
                                                            background: 'white', color: '#173f69', fontSize: '0.78rem',
                                                            fontWeight: 700, cursor: 'pointer'
                                                        }}
                                                    >
                                                        Apply rank defaults
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            <div style={{ ...CARD_STYLE, marginBottom: '1.25rem' }}>
                                <label style={{ ...LABEL_STYLE, marginBottom: '0.75rem', fontSize: '0.95rem' }}>
                                    <FaGraduationCap style={{ color: '#4a90e2' }} />
                                    Students <span style={{ background: '#dbeafe', color: '#1e40af', borderRadius: '20px', padding: '0.1rem 0.6rem', fontSize: '0.8rem', fontWeight: 700, marginLeft: '0.25rem' }}>{basicData.student_ids.length} selected</span>
                                </label>
                                <MultiSelectDropdown
                                    options={students} selectedIds={basicData.student_ids}
                                    onChange={ids => setBasicData(p => ({ ...p, student_ids: ids }))}
                                    placeholder="Click to select students…" loading={studentsLoading} type="student"
                                    filterRankName={selectedRank?.name}
                                />
                            </div>

                            <div style={CARD_STYLE}>
                                <label style={{ ...LABEL_STYLE, marginBottom: '0.75rem', fontSize: '0.95rem' }}>
                                    <FaUserTie style={{ color: '#4a90e2' }} />
                                    Preceptors <span style={{ background: '#dbeafe', color: '#1e40af', borderRadius: '20px', padding: '0.1rem 0.6rem', fontSize: '0.8rem', fontWeight: 700, marginLeft: '0.25rem' }}>{basicData.preceptor_ids.length} selected</span>
                                </label>
                                <MultiSelectDropdown
                                    options={preceptors} selectedIds={basicData.preceptor_ids}
                                    onChange={ids => setBasicData(p => ({ ...p, preceptor_ids: ids }))}
                                    placeholder="Click to select preceptors…" loading={preceptorsLoading} type="preceptor"
                                    filterRankId={basicData.rankId} filterRankName={selectedRank?.name} allRanks={ranks}
                                />
                            </div>
                        </div>
                    )}

                    {/* ── STAGE 2: Configuration ── */}
                    {currentStep === 2 && (
                        <div style={{ maxWidth: 720, margin: '0 auto', animation: 'mc-rise 0.25s ease-out' }}>
                            <h3 style={{ margin: '0 0 0.3rem', fontSize: '1.35rem', fontWeight: 700, color: '#1f2937' }}>Configuration</h3>
                            <p style={{ margin: '0 0 1.75rem', color: '#6b7280' }}>Configure rotations, scheduling, and program settings</p>

                            {/* Rotations count */}
                            <div style={{ ...CARD_STYLE, marginBottom: '1.25rem' }}>
                                <h4 style={{ margin: '0 0 1rem', color: '#374151', fontSize: '1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <FaCog style={{ color: '#4a90e2' }} /> Rotations
                                </h4>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem' }}>
                                    <span style={{ fontWeight: 600, color: '#374151', minWidth: 130 }}>Number of Rotations</span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <button type="button" onClick={() => updateRotationCount(configData.rotationsCount - 1)}
                                            style={{ width: 36, height: 36, borderRadius: 8, border: '2px solid #e5e7eb', background: 'white', cursor: 'pointer', fontWeight: 700, fontSize: '1.1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#374151' }}>−</button>
                                        <input type="number" min={1} max={16} value={configData.rotationsCount}
                                            onChange={e => updateRotationCount(Number(e.target.value))}
                                            style={{ ...FIELD_STYLE, width: 72, textAlign: 'center', padding: '0.5rem', fontSize: '1.1rem', fontWeight: 700 }}
                                            onFocus={focusField} onBlur={blurField} />
                                        <button type="button" onClick={() => updateRotationCount(configData.rotationsCount + 1)}
                                            style={{ width: 36, height: 36, borderRadius: 8, border: '2px solid #e5e7eb', background: 'white', cursor: 'pointer', fontWeight: 700, fontSize: '1.1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#374151' }}>+</button>
                                    </div>
                                </div>

                                {/* Rotation date table */}
                                <div style={{ display: 'grid', gap: '0.75rem', maxHeight: 300, overflowY: 'auto' }}>
                                    {ensureRotations(configData.rotationsCount, configData.rotations).map((rot, i) => (
                                        <div key={i} style={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr', gap: '0.75rem', alignItems: 'center', padding: '0.75rem', background: '#f8fafc', borderRadius: 10, border: '1px solid #e5e7eb' }}>
                                            <span style={{ fontWeight: 700, color: 'white', fontSize: '0.88rem', textAlign: 'center', background: 'linear-gradient(135deg,#173f69,#255886)', borderRadius: 8, padding: '0.3rem 0.5rem' }}>R{i + 1}</span>
                                            <div>
                                                <div style={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: 600, marginBottom: '0.2rem' }}>Start</div>
                                                <input type="date" value={rot.startDate || ''} onChange={e => updateRotationDate(i, 'startDate', e.target.value)}
                                                    style={{ ...FIELD_STYLE, padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                                                    onFocus={focusField} onBlur={blurField} />
                                            </div>
                                            <div>
                                                <div style={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: 600, marginBottom: '0.2rem' }}>End</div>
                                                <input type="date" value={rot.endDate || ''} onChange={e => updateRotationDate(i, 'endDate', e.target.value)}
                                                    style={{ ...FIELD_STYLE, padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                                                    onFocus={focusField} onBlur={blurField} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Form registration & choices */}
                            <div style={{ ...CARD_STYLE, marginBottom: '1.25rem' }}>
                                <h4 style={{ margin: '0 0 1rem', color: '#374151', fontSize: '1rem', fontWeight: 700 }}>Registration & Choices</h4>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                                    <div>
                                        <label style={LABEL_STYLE}><FaCalendarAlt style={{ color: '#4a90e2' }} /> Form Reg. Start</label>
                                        <input type="date" value={configData.form_registration?.startDate || ''}
                                            onChange={e => setConfigData(p => ({ ...p, form_registration: { ...p.form_registration, startDate: e.target.value } }))}
                                            style={FIELD_STYLE} onFocus={focusField} onBlur={blurField} />
                                    </div>
                                    <div>
                                        <label style={LABEL_STYLE}><FaCalendarAlt style={{ color: '#4a90e2' }} /> Form Reg. End</label>
                                        <input type="date" value={configData.form_registration?.endDate || ''}
                                            onChange={e => setConfigData(p => ({ ...p, form_registration: { ...p.form_registration, endDate: e.target.value } }))}
                                            style={FIELD_STYLE} onFocus={focusField} onBlur={blurField} />
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                    <div>
                                        <label style={LABEL_STYLE}>Choices per Student</label>
                                        <input type="number" min={1} max={20} value={configData.choicesCount}
                                            onChange={e => setConfigData(p => ({ ...p, choicesCount: Number(e.target.value) }))}
                                            style={FIELD_STYLE} onFocus={focusField} onBlur={blurField} />
                                    </div>
                                </div>
                            </div>

                            {/* Hours */}
                            <div style={{ ...CARD_STYLE, marginBottom: '1.25rem' }}>
                                <h4 style={{ margin: '0 0 1rem', color: '#374151', fontSize: '1rem', fontWeight: 700 }}>Default Hours</h4>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                                    <div>
                                        <label style={LABEL_STYLE}>Full-Time Start</label>
                                        <input type="time" value={configData.defaultFullTimeHrs?.start_time || '08:00'}
                                            onChange={e => setConfigData(p => ({ ...p, defaultFullTimeHrs: { ...p.defaultFullTimeHrs, start_time: e.target.value } }))}
                                            style={FIELD_STYLE} onFocus={focusField} onBlur={blurField} />
                                    </div>
                                    <div>
                                        <label style={LABEL_STYLE}>Full-Time End</label>
                                        <input type="time" value={configData.defaultFullTimeHrs?.end_time || '16:00'}
                                            onChange={e => setConfigData(p => ({ ...p, defaultFullTimeHrs: { ...p.defaultFullTimeHrs, end_time: e.target.value } }))}
                                            style={FIELD_STYLE} onFocus={focusField} onBlur={blurField} />
                                    </div>
                                    <div>
                                        <label style={LABEL_STYLE}>Part-Time Start</label>
                                        <input type="time" value={configData.defaultPartTimeHrs?.start_time || '08:00'}
                                            onChange={e => setConfigData(p => ({ ...p, defaultPartTimeHrs: { ...p.defaultPartTimeHrs, start_time: e.target.value } }))}
                                            style={FIELD_STYLE} onFocus={focusField} onBlur={blurField} />
                                    </div>
                                    <div>
                                        <label style={LABEL_STYLE}>Part-Time End</label>
                                        <input type="time" value={configData.defaultPartTimeHrs?.end_time || '12:00'}
                                            onChange={e => setConfigData(p => ({ ...p, defaultPartTimeHrs: { ...p.defaultPartTimeHrs, end_time: e.target.value } }))}
                                            style={FIELD_STYLE} onFocus={focusField} onBlur={blurField} />
                                    </div>
                                </div>
                            </div>

                            {/* Toggles */}
                            <div style={{ display: 'grid', gap: '0.75rem' }}>
                                <Toggle value={configData.spep_prefs} onChange={v => setConfigData(p => ({ ...p, spep_prefs: v }))} label="SPEP Preferences" />
                                <Toggle value={configData.sitePreceptorOnly} onChange={v => setConfigData(p => ({ ...p, sitePreceptorOnly: v }))} label="Site-Preceptor Only Matching" />
                            </div>
                        </div>
                    )}

                    {/* ── STAGE 3: Constraints ── */}
                    {currentStep === 3 && (
                        <div style={{ maxWidth: 720, margin: '0 auto', animation: 'mc-rise 0.25s ease-out' }}>
                            <h3 style={{ margin: '0 0 0.3rem', fontSize: '1.35rem', fontWeight: 700, color: '#1f2937' }}>Rotation Constraints</h3>
                            <p style={{ margin: '0 0 1.75rem', color: '#6b7280' }}>
                                {selectedRank
                                    ? `${selectedRank.name} is matched by ${matchByMeta.label.toLowerCase()}, so constraints target ${matchByMeta.pluralLabel.toLowerCase()}.`
                                    : 'Select a rank in the first step to configure constraints.'}
                            </p>

                            {!selectedRank ? (
                                <div style={{ textAlign: 'center', padding: '1.5rem', color: '#9ca3af', fontSize: '0.9rem', border: '2px dashed #e5e7eb', borderRadius: 12 }}>
                                    No rank selected — go back to Basic Info and pick one
                                </div>
                            ) : (
                                <div style={{ ...CARD_STYLE, marginBottom: '1.5rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                                        <h4 style={{ margin: 0, color: '#1e3a8a', fontSize: '1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <span style={{ background: getRankBadgeColor(selectedRank.name), color: 'white', borderRadius: 8, padding: '0.2rem 0.6rem', fontSize: '0.75rem', fontWeight: 700 }}>{selectedRank.name}</span>
                                            {matchByMeta.label} Constraints
                                        </h4>
                                        <button type="button" onClick={addConstraint} style={{
                                            display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 0.9rem',
                                            border: 'none', borderRadius: 10, background: 'linear-gradient(135deg,#173f69,#255886)',
                                            color: 'white', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer',
                                        }}>
                                            <FaPlus style={{ fontSize: '0.7rem' }} /> Add Constraint
                                        </button>
                                    </div>

                                    {constraintsData.constraints.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '1.5rem', color: '#9ca3af', fontSize: '0.9rem', border: '2px dashed #e5e7eb', borderRadius: 12 }}>
                                            No constraints yet — click "Add Constraint" to begin
                                        </div>
                                    ) : constraintsData.constraints.map(c => (
                                        <div key={c.id} style={{ border: '1.5px solid #dbeafe', borderRadius: 12, padding: '1rem', marginBottom: '0.75rem', background: '#f8faff' }}>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '0.75rem', marginBottom: '0.75rem' }}>
                                                <div>
                                                    <div style={{ ...LABEL_STYLE, fontSize: '0.78rem' }}>Minimum</div>
                                                    <input type="number" min={1} value={c.minimumNumber} onChange={e => updateConstraint(c.id, 'minimumNumber', e.target.value)}
                                                        style={{ ...FIELD_STYLE, padding: '0.55rem 0.75rem', fontSize: '0.9rem' }} onFocus={focusField} onBlur={blurField} />
                                                </div>
                                                <div>
                                                    <div style={{ ...LABEL_STYLE, fontSize: '0.78rem' }}>From Rotation</div>
                                                    <input type="number" min={1} max={configData.rotationsCount} value={c.fromRotation} onChange={e => updateConstraint(c.id, 'fromRotation', e.target.value)}
                                                        style={{ ...FIELD_STYLE, padding: '0.55rem 0.75rem', fontSize: '0.9rem' }} onFocus={focusField} onBlur={blurField} />
                                                </div>
                                                <div>
                                                    <div style={{ ...LABEL_STYLE, fontSize: '0.78rem' }}>To Rotation</div>
                                                    <input type="number" min={1} max={configData.rotationsCount} value={c.toRotation} onChange={e => updateConstraint(c.id, 'toRotation', e.target.value)}
                                                        style={{ ...FIELD_STYLE, padding: '0.55rem 0.75rem', fontSize: '0.9rem' }} onFocus={focusField} onBlur={blurField} />
                                                </div>
                                                <button type="button" onClick={() => removeConstraint(c.id)} style={{
                                                    width: 36, height: 36, border: 'none', borderRadius: 10, background: '#fee2e2',
                                                    color: '#dc2626', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', alignSelf: 'end'
                                                }}>
                                                    <FaTrash style={{ fontSize: '0.8rem' }} />
                                                </button>
                                            </div>
                                            <div>
                                                <div style={{ ...LABEL_STYLE, fontSize: '0.78rem' }}>{matchByMeta.pluralLabel}</div>
                                                <EntityChipSelect items={constraintEntities} selectedIds={c.entities}
                                                    onChange={ids => updateConstraint(c.id, 'entities', ids)}
                                                    loading={constraintEntitiesLoading}
                                                    placeholder={`Search ${matchByMeta.pluralLabel.toLowerCase()}…`} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    {/* ── STAGE 4: Forms ── */}
                    {currentStep === 4 && (
                        <div style={{ maxWidth: 780, margin: '0 auto', animation: 'mc-rise 0.25s ease-out' }}>
                            <h3 style={{ margin: '0 0 0.3rem', fontSize: '1.35rem', fontWeight: 700, color: '#1f2937' }}>Attached Forms</h3>
                            <p style={{ margin: '0 0 1.75rem', color: '#6b7280' }}>
                                Attach form templates to this curriculum, each with its own schedule and audience. Changes here save immediately —
                                they aren't part of the "Update Curriculum" submit on the last step.
                            </p>
                            <CurriculumFormsPanel
                                curriculumId={curriculum._id}
                                curriculumName={basicData.name || curriculum.name}
                            />
                        </div>
                    )}
                    {/* ── STAGE 5: Review ── */}
                    {currentStep === 5 && (
                        <div style={{ maxWidth: 640, margin: '0 auto', animation: 'mc-rise 0.25s ease-out' }}>
                            <h3 style={{ margin: '0 0 0.3rem', fontSize: '1.35rem', fontWeight: 700, color: '#1f2937' }}>Review Changes</h3>
                            <p style={{ margin: '0 0 1.75rem', color: '#6b7280' }}>Confirm everything looks good before saving</p>

                            <div style={{ display: 'grid', gap: '1rem' }}>
                                <div style={CARD_STYLE}>
                                    <h4 style={{ margin: '0 0 1rem', color: '#374151', fontSize: '0.95rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <FaBook style={{ color: '#4a90e2' }} /> Curriculum Details
                                    </h4>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', fontSize: '0.9rem' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}><strong>Name</strong><span>{basicData.name || '—'}</span></div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}><strong>Rank</strong><span style={{ background: getRankBadgeColor(selectedRank?.name), color: 'white', padding: '0.15rem 0.6rem', borderRadius: 12, fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase' }}>{selectedRank?.name || '—'}</span></div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}><strong>Period</strong><span>{basicData.start_date} → {basicData.end_date}</span></div>
                                    </div>
                                </div>

                                <div style={CARD_STYLE}>
                                    <h4 style={{ margin: '0 0 1rem', color: '#374151', fontSize: '0.95rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <FaUsers style={{ color: '#4a90e2' }} /> Assignments
                                    </h4>
                                    <div style={{ display: 'flex', gap: '1rem' }}>
                                        <div style={{ flex: 1, textAlign: 'center', padding: '1rem', background: '#eff6ff', borderRadius: 12, border: '1px solid #bfdbfe' }}>
                                            <div style={{ fontSize: '2rem', fontWeight: 800, color: '#1e40af' }}>{basicData.student_ids.length}</div>
                                            <div style={{ fontSize: '0.8rem', color: '#3b82f6', fontWeight: 600 }}>Students</div>
                                        </div>
                                        <div style={{ flex: 1, textAlign: 'center', padding: '1rem', background: '#f0fdf4', borderRadius: 12, border: '1px solid #bbf7d0' }}>
                                            <div style={{ fontSize: '2rem', fontWeight: 800, color: '#15803d' }}>{basicData.preceptor_ids.length}</div>
                                            <div style={{ fontSize: '0.8rem', color: '#16a34a', fontWeight: 600 }}>Preceptors</div>
                                        </div>
                                    </div>
                                </div>

                                <div style={CARD_STYLE}>
                                    <h4 style={{ margin: '0 0 1rem', color: '#374151', fontSize: '0.95rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <FaCog style={{ color: '#4a90e2' }} /> Configuration
                                    </h4>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', fontSize: '0.9rem' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}><strong>Rotations</strong><span>{configData.rotationsCount}</span></div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}><strong>Choices per Student</strong><span>{configData.choicesCount}</span></div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}><strong>SPEP Preferences</strong><span style={{ color: configData.spep_prefs ? '#16a34a' : '#9ca3af', fontWeight: 700 }}>{configData.spep_prefs ? 'Enabled' : 'Disabled'}</span></div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}><strong>Site-Preceptor Only</strong><span style={{ color: configData.sitePreceptorOnly ? '#16a34a' : '#9ca3af', fontWeight: 700 }}>{configData.sitePreceptorOnly ? 'Enabled' : 'Disabled'}</span></div>
                                    </div>
                                </div>

                                <div style={CARD_STYLE}>
                                    <h4 style={{ margin: '0 0 1rem', color: '#374151', fontSize: '0.95rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <FaClipboardList style={{ color: '#4a90e2' }} /> Constraints
                                    </h4>
                                    <div style={{ display: 'flex', gap: '1rem' }}>
                                        <div style={{ flex: 1, textAlign: 'center', padding: '0.875rem', background: '#eff6ff', borderRadius: 10, border: '1px solid #bfdbfe' }}>
                                            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#1e3a8a' }}>{constraintsData.constraints.length}</div>
                                            <div style={{ fontSize: '0.78rem', color: '#3b82f6', fontWeight: 700 }}>
                                                {selectedRank?.name || 'Rank'} · by {matchByMeta.label.toLowerCase()}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ padding: '1rem 1.25rem', background: 'linear-gradient(135deg,#f0fdf4,#dcfce7)', border: '1.5px solid #bbf7d0', borderRadius: 14, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                    <FaCheckCircle style={{ color: '#16a34a', fontSize: '1.2rem', flexShrink: 0 }} />
                                    <span style={{ fontSize: '0.9rem', color: '#15803d', fontWeight: 600 }}>All changes are staged. Click "Update Curriculum" to save.</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Footer ── */}
                <div style={{
                    padding: '1.25rem 2.5rem', borderTop: '1px solid #e5e7eb',
                    background: '#f9fafb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem',
                }}>
                    <button onClick={() => onCancel?.()} style={{
                        padding: '0.8rem 1.5rem', border: '2px solid #e5e7eb', borderRadius: 12,
                        background: 'white', color: '#374151', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600, transition: 'all 0.2s',
                    }}
                        onMouseEnter={e => { e.target.style.borderColor = '#3b82f6'; e.target.style.color = '#1e40af'; e.target.style.background = '#eff6ff'; }}
                        onMouseLeave={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.color = '#374151'; e.target.style.background = 'white'; }}>
                        Cancel
                    </button>

                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                        <button onClick={() => currentStep > 1 && setCurrentStep(p => p - 1)} disabled={currentStep === 1}
                            style={{
                                padding: '0.8rem 1.4rem', border: '2px solid #e5e7eb', borderRadius: 12, background: currentStep === 1 ? '#f3f4f6' : 'white',
                                color: currentStep === 1 ? '#9ca3af' : '#374151', cursor: currentStep === 1 ? 'not-allowed' : 'pointer',
                                fontSize: '0.9rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.4rem', transition: 'all 0.2s',
                            }}>
                            <FaArrowLeft /> Previous
                        </button>

                        {currentStep < TOTAL_STEPS ? (
                            <button onClick={() => { if (validateStep()) setCurrentStep(p => p + 1); }}
                                style={{
                                    padding: '0.8rem 1.75rem', border: 'none', borderRadius: 12,
                                    background: 'linear-gradient(135deg,#173f69,#255886)', color: 'white',
                                    cursor: 'pointer', fontSize: '0.9rem', fontWeight: 700,
                                    display: 'flex', alignItems: 'center', gap: '0.4rem',
                                    boxShadow: '0 4px 14px rgba(23,63,105,0.35)', transition: 'all 0.2s',
                                }}
                                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(23,63,105,0.45)'; }}
                                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(23,63,105,0.35)'; }}>
                                Next <FaArrowRight />
                            </button>
                        ) : (
                            <button onClick={() => handleSubmit(false)} disabled={saving}
                                style={{
                                    padding: '0.8rem 1.75rem', border: 'none', borderRadius: 12,
                                    background: saving ? '#d1d5db' : 'linear-gradient(135deg,#15803d,#16a34a)',
                                    color: 'white', cursor: saving ? 'not-allowed' : 'pointer',
                                    fontSize: '0.9rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.4rem',
                                    boxShadow: saving ? 'none' : '0 4px 14px rgba(22,163,74,0.35)', transition: 'all 0.2s',
                                }}
                                onMouseEnter={e => { if (!saving) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(22,163,74,0.45)'; } }}
                                onMouseLeave={e => { if (!saving) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(22,163,74,0.35)'; } }}>
                                {saving ? <><FaSpinner style={{ animation: 'mc-spin 0.85s linear infinite' }} /> Saving…</> : <><FaSave /> Update Curriculum</>}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {rotationWarning && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(9, 26, 43, 0.18)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.25rem', zIndex: 2000,
                }}>
                    <div style={{
                        width: 'min(680px, 100%)', background: 'linear-gradient(180deg,#ffffff 0%,#f7fbff 100%)',
                        borderRadius: 20, overflow: 'hidden',
                        border: '1px solid rgba(193, 212, 233, 0.95)',
                        boxShadow: '0 14px 34px rgba(22, 54, 86, 0.12)'
                    }}>
                        <div style={{ padding: '1.35rem 1.5rem', background: 'linear-gradient(135deg,#f59e0b,#fbbf24)', borderBottom: '1px solid rgba(202,138,4,0.22)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                <div style={{ width: 42, height: 42, borderRadius: '14px', background: 'rgba(255,255,255,0.22)', color: '#7c2d12', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.05rem', fontWeight: 800, border: '1px solid rgba(255,255,255,0.35)' }}>
                                    <FaExclamationTriangle />
                                </div>
                                <div>
                                    <div style={{ fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#7c2d12' }}>Confirm rotation reduction</div>
                                    <h3 style={{ margin: '0.15rem 0 0', fontSize: '1.2rem', color: '#4a2508' }}>Student assignments exist in rotations that will be removed</h3>
                                </div>
                            </div>
                        </div>

                        <div style={{ padding: '1.4rem 1.5rem 1.5rem' }}>
                            <p style={{ margin: '0 0 1rem', color: '#475569', lineHeight: 1.6 }}>
                                {rotationWarning.warning || 'Reducing the rotation count would remove existing assigned slots.'}
                            </p>

                            {rotationWarning.conflict && (
                                <div style={{ background: 'linear-gradient(180deg,#ffffff 0%,#f8fbff 100%)', border: '1px solid rgba(193, 212, 233, 0.9)', borderRadius: 16, padding: '1rem 1.1rem', marginBottom: '1rem' }}>
                                    <div style={{ fontSize: '0.76rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.55rem' }}>Example impact</div>
                                    <div style={{ display: 'grid', gap: '0.5rem', color: '#15324f', fontSize: '0.94rem' }}>
                                        <div><strong>Student:</strong> {rotationWarning.conflict.student_name || rotationWarning.conflict.student_id}</div>
                                        <div><strong>Deleted rotations:</strong> {Array.isArray(rotationWarning.conflict.conflict_rotations) ? rotationWarning.conflict.conflict_rotations.map(r => `R${r}`).join(', ') : '—'}</div>
                                    </div>
                                </div>
                            )}

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', flexWrap: 'wrap' }}>
                                <button
                                    type="button"
                                    onClick={() => setRotationWarning(null)}
                                    disabled={saving}
                                    style={{
                                        padding: '0.8rem 1.2rem', borderRadius: 12, border: '1px solid rgba(193, 212, 233, 0.95)', background: 'white',
                                        color: '#355a80', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    Go back
                                </button>
                                <button
                                    type="button"
                                    onClick={async () => {
                                        setRotationWarning(null);
                                        await handleSubmit(true);
                                    }}
                                    disabled={saving}
                                    style={{
                                        padding: '0.8rem 1.3rem', borderRadius: 12, border: 'none',
                                        background: 'linear-gradient(135deg,#dc2626,#ef4444)', color: 'white', fontWeight: 800,
                                        cursor: saving ? 'not-allowed' : 'pointer', boxShadow: '0 10px 22px rgba(220,38,38,0.22)'
                                    }}
                                >
                                    {saving ? <><FaSpinner style={{ animation: 'mc-spin 0.85s linear infinite' }} /> Saving…</> : 'Confirm and save anyway'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EditCurriculumModern;
